/**
 * Shared logic for column list structure (StatConfigPickers and SelectedColumnsList).
 * Both use identical grouping, sorting, and structure; only rendering differs (checkbox vs read-only).
 */
import type { AnalyticsStatMetadata } from '~/hooks/queries/useAnalyticsStatMetadataQuery';
import type { ColumnRef } from '~/hooks/queries/useAnalyticsStatsViewsQuery';
import {
  columnTooltipForPicker,
  getColumnRefLabel,
  getDisplayColumnsForDimension,
  type TaggedItem,
} from './analyticsStatsColumnUtils';

export const tagHeaderSx = {
  py: 0.5,
  px: 1,
  borderLeft: '3px solid',
  borderColor: 'dianomiGreen.main',
  bgcolor: 'grey.100',
  borderRadius: '0 2px 2px 0',
} as const;

/** Shared by StatConfig, staff column catalog, and ColumnPickerToggleAllButton. */
export const columnPickerToggleAllButtonSx = {
  minWidth: 0,
  p: 0,
  textTransform: 'none',
  fontSize: '0.75rem',
  color: 'primary.main',
} as const;

/** Puts each item in exactly one group. Uses first tag. Items with no tags are excluded. Ordering handled by sortTagHeaders with API sort order. */
export function groupByPrimaryTag<T extends { tags?: string[] }>(items: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const tags = item.tags ?? [];
    const primaryTag = tags[0];
    if (primaryTag != null) {
      const existing = map.get(primaryTag) ?? [];
      existing.push(item);
      map.set(primaryTag, existing);
    }
  }
  return map;
}

/** Sorts tag headers using metadata sort order when provided. */
export function sortTagHeaders(headers: string[], tagSortOrder?: string[]): string[] {
  return [...headers].sort((headerA, headerB) => {
    if (!tagSortOrder?.length) return headerA.localeCompare(headerB);
    const indexA = tagSortOrder.indexOf(headerA);
    const indexB = tagSortOrder.indexOf(headerB);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return headerA.localeCompare(headerB);
  });
}

export type DimensionWithAttributes = {
  ref: ColumnRef & { kind: 'grouping' };
  attributes: TaggedItem[];
  label: string;
};

/**
 * Expand-row label for a dimension with display attributes (e.g. "Creative Id" → "Creative").
 * When every attribute shares one primary tag, uses that tag; otherwise strips a trailing " Id" from the dimension label.
 */
export function getDimensionGroupHeaderLabel(
  dimensionLabel: string,
  attributes: TaggedItem[],
): string {
  const groups = groupByPrimaryTag(attributes);
  if (groups.size === 1) {
    const tagHeader = groups.keys().next().value;
    if (tagHeader != null) {
      return tagHeader;
    }
  }
  const baseName = dimensionLabel.replace(/\s+Id$/i, '').trim();
  return baseName || dimensionLabel;
}

/**
 * Computes the column list structure from columnLayout and metadata.
 * Used by both StatConfigPickers (picker mode) and SelectedColumnsList (read-only).
 *
 * Picker mode: includes all dimensions from metadata with their full attribute options;
 * only includes dimensions/attributes that exist in metadata.
 * Read-only mode: includes only dimensions and attributes present in columnLayout.
 */
export function computeColumnListStructure(
  columnLayout: ColumnRef[],
  metadata: AnalyticsStatMetadata,
  mode: 'picker' | 'read-only',
) {
  const groupingRefs = columnLayout.filter((layoutRef) => layoutRef.kind === 'grouping');
  const statRefs = columnLayout.filter((layoutRef) => layoutRef.kind === 'stat');
  const displayRefs = columnLayout.filter((layoutRef) => layoutRef.kind === 'display');

  const dimensionOrder = metadata.allowed.groupBy;
  const dimensions: DimensionWithAttributes[] = [];

  for (const groupKey of dimensionOrder) {
    const group = metadata.groupingsByKey[groupKey];
    // eslint-disable-next-line no-continue
    if (!group) continue;
    const ref: ColumnRef & { kind: 'grouping' } = { kind: 'grouping', key: groupKey };
    const label = group?.label ?? groupKey;

    if (mode === 'picker') {
      const displayAliases = getDisplayColumnsForDimension(metadata, groupKey);
      const attributes: TaggedItem[] = displayAliases.map((columnAlias) => {
        const column = metadata.displayColumnsByAlias[columnAlias];
        return {
          id: columnAlias,
          label: column?.label ?? columnAlias,
          tooltip: columnTooltipForPicker(column?.tooltip),
          tags: column?.tags,
        };
      });
      dimensions.push({ ref, attributes, label });
    } else {
      const inLayout = groupingRefs.some((layoutRef) => layoutRef.key === groupKey);
      // eslint-disable-next-line no-continue
      if (!inLayout) continue;

      const attributeRefs = displayRefs.filter((layoutRef) => {
        const column = metadata.displayColumnsByAlias[layoutRef.alias];
        return column?.idColumnAlias === groupKey;
      });
      const attributes: TaggedItem[] = attributeRefs.map((layoutRef) => {
        const column = metadata.displayColumnsByAlias[layoutRef.alias];
        return {
          id: layoutRef.alias,
          label: getColumnRefLabel(layoutRef, metadata) ?? layoutRef.alias,
          tooltip: columnTooltipForPicker(column?.tooltip),
          tags: column?.tags,
        };
      });
      dimensions.push({ ref, attributes, label });
    }
  }

  const statItems: TaggedItem[] =
    mode === 'picker'
      ? metadata.allowed.statColumns.map((columnAlias) => {
          const column = metadata.statColumnsByAlias[columnAlias];
          return {
            id: columnAlias,
            label: column?.label ?? columnAlias,
            tooltip: columnTooltipForPicker(column?.tooltip),
            tags: column?.tags,
          };
        })
      : statRefs.map((statRef) => {
          const column = metadata.statColumnsByAlias[statRef.alias];
          return {
            id: statRef.alias,
            label: getColumnRefLabel(statRef, metadata) ?? statRef.alias,
            tooltip: columnTooltipForPicker(column?.tooltip),
            tags: column?.tags,
          };
        });

  const metricsByHeader = groupByPrimaryTag(statItems);
  const metricHeaders = sortTagHeaders(
    Array.from(metricsByHeader.keys()),
    metadata.statColumnTagsSortOrder,
  );

  return {
    dimensions,
    metricsByHeader,
    metricHeaders,
  };
}
